import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import { useQuoting } from '../hooks/useQuoting';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import Tooltip from '../components/common/Tooltip';
import {
  buildQuoteDraft,
  emptyAnswers,
  getQuoteSettings,
  rankQuoteWithAI,
  refreshQuoteStock,
  SECTION_META,
  STOCK_META,
  STOCK_SOURCE_LABELS,
  toggleChoice,
  updateQuoteDraft
} from '../utils/quotes';

const money = value => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD'
}).format(Number(value) || 0);

const dateLabel = value => {
  if (!value) return '-';
  const date = typeof value?.toDate === 'function' ? value.toDate() :
    value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const errorMessage = err => err?.message || 'The quote request failed.';

function AnswerQuestion({ questionKey, question, answer, onChange }) {
  const selected = question.options.filter(option => answer.choices.includes(option.id));
  const selectedTiming = questionKey === 'timing' ? selected[0] : null;
  const needsDate = selectedTiming &&
    ['event', 'current_season', 'next_season'].includes(selectedTiming.kind);

  return (
    <section className="rounded-lg border bg-white p-4">
      <h2 className="mb-3 text-base font-semibold text-gray-900">{question.label}</h2>
      <div className="flex flex-wrap gap-2">
        {question.options.map(option => {
          const active = answer.choices.includes(option.id);
          return (
            <button key={option.id} type="button" aria-pressed={active}
              onClick={() => onChange(toggleChoice(answer, option.id, question.multi))}
              className={`rounded-full border px-3 py-1.5 text-sm ${
                active ? 'border-indigo-600 bg-indigo-50 text-indigo-800' :
                  'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}>
              {option.label}
            </button>
          );
        })}
      </div>
      <label className="mt-3 block text-sm text-gray-700">
        Other details
        <textarea value={answer.other}
          onChange={event => onChange({ ...answer, other: event.target.value })}
          maxLength={300} rows={2}
          className="mt-1 w-full rounded border border-gray-300 p-2"
          placeholder="Add context or another answer" />
      </label>
      {needsDate && (
        <label className="mt-3 block max-w-xs text-sm text-gray-700">
          {selectedTiming.kind === 'event' ? 'Event date' : 'Target date'}
          <input type="date" value={answer.date || ''}
            onChange={event => onChange({ ...answer, date: event.target.value })}
            className="mt-1 block w-full rounded border border-gray-300 p-2" />
        </label>
      )}
      {questionKey === 'budget' && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm text-gray-700">
            Exact budget (optional)
            <input type="number" min="1" step="0.01" value={answer.amount}
              onChange={event => onChange({ ...answer, amount: event.target.value })}
              className="mt-1 block w-full rounded border border-gray-300 p-2" />
          </label>
          <label className="text-sm text-gray-700">
            Target units (optional)
            <input type="number" min="1" step="1" value={answer.units}
              onChange={event => onChange({ ...answer, units: event.target.value })}
              className="mt-1 block w-full rounded border border-gray-300 p-2" />
          </label>
        </div>
      )}
    </section>
  );
}

function QuoteDetail({ quoteId, user, canEdit, quoteAiEnabled, onBack }) {
  const [quote, setQuote] = useState(null);
  const [draftLines, setDraftLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, collectionPath('quotes', quoteId)),
      snapshot => {
        if (!snapshot.exists()) {
          setError('This quote was not found or you do not have access.');
          setQuote(null);
          return;
        }
        const data = { ...snapshot.data(), quoteId: snapshot.id };
        setQuote(data);
        if (!dirty) {
          setDraftLines((data.lines || []).map(line => ({ ...line, removed: false })));
          setNotes(data.notes || '');
          setStatus(data.status || 'draft');
        }
        setError('');
      },
      err => {
        console.error('Quote listener failed:', err);
        setError('Unable to load this quote. Check your access and try again.');
      }
    );
    return () => unsubscribe();
  }, [quoteId, dirty]);

  const setQuantity = (lineId, skuId, value) => {
    setDraftLines(lines => lines.map(line => line.lineId !== lineId ? line : {
      ...line,
      sizes: line.sizes.map(size => size.skuId === skuId
        ? { ...size, qty: value === '' ? '' : Math.max(0, Math.floor(Number(value) || 0)) }
        : size)
    }));
    setDirty(true);
  };

  const removeLine = lineId => {
    setDraftLines(lines => lines.map(line =>
      line.lineId === lineId ? { ...line, removed: true } : line));
    setDirty(true);
  };

  const save = async () => {
    setBusy('save');
    setError('');
    setMessage('');
    try {
      await updateQuoteDraft({
        quoteId,
        edits: { lines: draftLines.map(line => ({
          lineId: line.lineId,
          remove: line.removed,
          sizes: line.sizes.map(size => ({
            skuId: size.skuId,
            qty: Number(size.qty) || 0
          }))
        })) },
        notes,
        status
      });
      setDirty(false);
      setMessage('Quote saved.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  const refreshStock = async () => {
    setBusy('stock');
    setError('');
    setMessage('');
    try {
      const result = await refreshQuoteStock(quoteId);
      setMessage(`Stock refreshed. ${result.shortLines} line(s) need attention.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  const rank = async () => {
    setBusy('ai');
    setError('');
    setMessage('');
    try {
      const result = await rankQuoteWithAI(quoteId);
      setMessage(result.status === 'ok' ? 'AI ranking is ready.' :
        `AI ranking status: ${result.status || 'unavailable'}. The rules quote remains available.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(quote?.ai?.customerSummary || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Unable to copy customer quote summary:', err);
      setError('Could not copy the summary. Select and copy the text instead.');
    }
  };

  const lineOrder = Object.keys(SECTION_META);
  const lineCount = draftLines.filter(line => !line.removed).length;
  const writable = canEdit && quote?.status !== 'archived';
  if (!quote) {
    return (
      <main className="mx-auto max-w-5xl p-4">
        {error ? <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p> :
          <p>Loading quote…</p>}
        <button type="button" className="mt-4 text-indigo-700 underline" onClick={onBack}>Back</button>
      </main>
    );
  }

  const rankings = new Map((quote.ai?.ranking || []).map(item => [item.lineId, item]));
  return (
    <main className="mx-auto max-w-6xl space-y-5 p-3 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button type="button" className="mb-2 text-sm text-indigo-700 underline" onClick={onBack}>Back</button>
          <h1 className="text-2xl font-bold text-gray-900">Quote for {quote.customerName || quote.customerId}</h1>
          <p className="text-sm text-gray-600">
            {quote.opportunityName ? `${quote.opportunityName} · ` : ''}
            Draft {quote.quoteId} · Built {dateLabel(quote.asOfDate)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {writable && (
            <Tooltip text="Fetch current ApparelMagic availability for quoted SKUs">
              <button type="button" disabled={Boolean(busy) || dirty} onClick={refreshStock}
                className="rounded border border-indigo-600 px-3 py-2 text-sm text-indigo-700 disabled:opacity-50">
                {busy === 'stock' ? 'Checking…' : 'Refresh live stock'}
              </button>
            </Tooltip>
          )}
          {quoteAiEnabled && writable && (
            <Tooltip text="Paid AI ranks and explains existing catalog lines; it does not add products">
              <button type="button" disabled={Boolean(busy) || dirty} onClick={rank}
                className="rounded bg-purple-700 px-3 py-2 text-sm text-white disabled:opacity-50">
                {busy === 'ai' ? 'Ranking…' : 'Rank with AI'}
              </button>
            </Tooltip>
          )}
          {writable && (
            <Tooltip text="Save the CRM quote draft; this does not send it or write to ApparelMagic">
              <button type="button" disabled={Boolean(busy) || !dirty} onClick={save}
                className="rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-50">
                {busy === 'save' ? 'Saving…' : 'Save draft'}
              </button>
            </Tooltip>
          )}
          <Tooltip text="Print this quote draft">
            <button type="button" onClick={() => window.print()}
              className="rounded border px-3 py-2 text-sm">Print</button>
          </Tooltip>
        </div>
      </div>

      {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p>}
      {message && <p role="status" className="rounded bg-green-50 p-3 text-green-800">{message}</p>}
      {!writable && <p className="rounded bg-gray-100 p-3 text-gray-700">This quote is read-only.</p>}

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
        <span>{STOCK_SOURCE_LABELS[quote.stockSource] || 'Stock source unavailable'}</span>
        <span>Checked: {dateLabel(quote.stockCheckedAt)}</span>
        <span>Season policy date: {quote.asOfDate}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded border bg-white p-3"><span className="text-sm text-gray-600">Lines</span><div className="text-xl font-semibold">{quote.totals?.lines || 0}</div></div>
        <div className="rounded border bg-white p-3"><span className="text-sm text-gray-600">Units</span><div className="text-xl font-semibold">{quote.totals?.units || 0}</div></div>
        <div className="rounded border bg-white p-3"><span className="text-sm text-gray-600">Draft total</span><div className="text-xl font-semibold">{money(quote.totals?.amount)}</div></div>
      </div>

      {!!quote.warnings?.length && (
        <section className="space-y-2" aria-label="Quote warnings">
          {quote.warnings.map((warning, index) => (
            <p key={`${warning.code}-${index}`}
              className={`rounded p-3 text-sm ${warning.severity === 'warning' ? 'bg-amber-50 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
              {warning.message}
            </p>
          ))}
        </section>
      )}

      {lineOrder.map(section => {
        const lines = draftLines.filter(line => line.section === section && !line.removed);
        if (!lines.length) return null;
        return (
          <section key={section} className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold">{SECTION_META[section].label}</h2>
              <p className="text-sm text-gray-600">{SECTION_META[section].description}</p>
            </div>
            {lines.map(line => (
              <article key={line.lineId} className="rounded-lg border bg-white p-4">
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold">{line.description || line.styleNumber} <span className="text-sm font-normal text-gray-500">({line.styleNumber})</span></h3>
                    <p className="text-sm text-gray-600">{line.category || 'Category not specified'} · {line.colorName || line.color} · {line.season || 'All Season'}</p>
                    <p className="mt-1 text-xs text-gray-500">{(line.reasons || []).join(' · ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{money(line.amount)}</p>
                    <span className={`rounded px-2 py-1 text-xs ${STOCK_META[line.stockStatus]?.className || 'bg-gray-100 text-gray-700'}`}>
                      {STOCK_META[line.stockStatus]?.label || line.stockStatus}
                    </span>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                  {line.sizes.map(size => (
                    <label key={size.skuId} className="rounded border p-2 text-sm">
                      <span className="block font-medium">{size.size || 'Size'}</span>
                      <span className="block text-xs text-gray-500">Available {size.available} · SKU {size.skuId}</span>
                      <input type="number" min="0" step="1" disabled={!writable}
                        aria-label={`${line.styleNumber} ${size.size} quantity`}
                        value={size.qty}
                        onChange={event => setQuantity(line.lineId, size.skuId, event.target.value)}
                        className="mt-1 w-full rounded border p-1" />
                    </label>
                  ))}
                </div>
                {rankings.has(line.lineId) && quote.ai?.status === 'ok' && (
                  <p className="mt-3 rounded bg-purple-50 p-3 text-sm text-purple-900">
                    AI rationale: {rankings.get(line.lineId).rationale}
                  </p>
                )}
                {writable && (
                  <Tooltip text="Remove this line from the editable quote draft">
                    <button type="button" onClick={() => removeLine(line.lineId)}
                      className="mt-3 text-sm text-red-700 underline">Remove line</button>
                  </Tooltip>
                )}
              </article>
            ))}
          </section>
        );
      })}
      {!lineCount && <p className="rounded border bg-white p-5 text-gray-700">No matching products were available for this request. Review the answers or run the catalog sync.</p>}
      {writable && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm font-medium">Draft status
            <select value={status} onChange={event => { setStatus(event.target.value); setDirty(true); }}
              className="mt-1 block w-full rounded border p-2">
              <option value="draft">Draft</option>
              <option value="ready">Ready for rep review</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label className="block text-sm font-medium">Internal notes
            <textarea value={notes} maxLength={2000}
              onChange={event => { setNotes(event.target.value); setDirty(true); }}
              rows={3} className="mt-1 block w-full rounded border p-2" />
          </label>
        </section>
      )}

      {quote.ai?.status && (
        <section className="space-y-3 rounded-lg border border-purple-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">AI quote review</h2>
            <span className="text-sm text-gray-600">{quote.ai.status === 'stale_input' ? 'Out of date — rerun after saving changes' : quote.ai.status}</span>
          </div>
          {quote.ai.status === 'ok' && quote.ai.customerSummary && (
            <>
              <div className="rounded bg-purple-50 p-4 text-gray-900">{quote.ai.customerSummary}</div>
              <Tooltip text="Copy the customer-facing draft summary; it is not sent">
                <button type="button" onClick={copySummary}
                  className="rounded border px-3 py-2 text-sm">{copied ? 'Copied' : 'Copy customer summary'}</button>
              </Tooltip>
            </>
          )}
          {!!quote.ai.conflicts?.length && (
            <ul className="list-inside list-disc space-y-1 text-sm">
              {quote.ai.conflicts.map((conflict, index) => <li key={index}>{conflict.message}</li>)}
            </ul>
          )}
        </section>
      )}
      {quoteAiEnabled && quote.ai?.status !== 'ok' && (
        <p className="text-sm text-gray-600">AI ranking is optional and costs tokens. It only ranks these catalog lines; it cannot add products or send this quote.</p>
      )}
      {user?.role === 'adminViewOnly' && <p className="text-sm text-gray-600">Admin - View Only can view quote drafts but cannot change them.</p>}
    </main>
  );
}

export default function QuoteBuilderPage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const quoting = useQuoting(currentUser);
  const [quoteSettings, setQuoteSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');
  const [answers, setAnswers] = useState(emptyAnswers);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const customerId = searchParams.get('customerId') || '';
  const opportunityId = searchParams.get('opportunityId') || '';
  const canBuild = quoting.canBuild && can(FEATURE_PERMISSIONS.QUOTES_BUILD);

  useEffect(() => {
    if (!quoting.addonEnabled) {
      setSettingsLoading(false);
      return undefined;
    }
    let active = true;
    getQuoteSettings().then(result => {
      if (active) setQuoteSettings(result.settings);
    }).catch(err => {
      if (active) setSettingsError(errorMessage(err));
    }).finally(() => {
      if (active) setSettingsLoading(false);
    });
    return () => { active = false; };
  }, [quoting.addonEnabled]);

  const updateAnswer = (key, answer) => setAnswers(current => ({ ...current, [key]: answer }));
  const create = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await buildQuoteDraft({ customerId, opportunityId, answers });
      navigate(`/quotes/${encodeURIComponent(result.quoteId)}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  if (quoteId) {
    if (!quoting.loaded) return <main className="p-6">Checking quote access…</main>;
    if (!quoting.canView) {
      return <main className="p-6">
        <p role="alert" className="rounded bg-gray-100 p-4 text-gray-700">
          The quote builder is not enabled for your account, or you do not have permission to view quotes.
        </p>
      </main>;
    }
    return <QuoteDetail quoteId={quoteId} user={currentUser}
      canEdit={canBuild} quoteAiEnabled={quoting.aiAvailable}
      onBack={() => navigate(-1)} />;
  }

  return (
    <main className="mx-auto max-w-5xl space-y-5 p-3 md:p-6">
      <div>
        <button type="button" className="mb-2 text-sm text-indigo-700 underline" onClick={() => navigate(-1)}>Back</button>
        <h1 className="text-2xl font-bold text-gray-900">Build a quote</h1>
        <p className="text-sm text-gray-600">Answer a few questions. Rules will build a draft from synced catalog items and customer order history. No quote is sent or written to ApparelMagic.</p>
      </div>
      {(!quoting.loaded || settingsLoading) && <p>Checking quote settings…</p>}
      {(quoting.error || settingsError) && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{quoting.error || settingsError}</p>}
      {quoting.loaded && !quoting.addonEnabled && <p className="rounded bg-gray-100 p-3">The AI Sales Agent add-on is not enabled for this tenant.</p>}
      {quoting.loaded && quoting.addonEnabled && !quoting.enabled && <p className="rounded bg-gray-100 p-3">The quote builder is not enabled by your CRM provider.</p>}
      {!settingsLoading && quoting.enabled && !canBuild && <p className="rounded bg-gray-100 p-3">You do not have permission to build quotes.</p>}
      {!settingsLoading && quoting.enabled && canBuild && quoteSettings && (
        <form onSubmit={create} className="space-y-4">
          {customerId ? <p className="rounded bg-indigo-50 p-3 text-sm text-indigo-900">Customer: {customerId}{opportunityId ? ` · Opportunity: ${opportunityId}` : ''}</p> :
            <p role="alert" className="rounded bg-amber-50 p-3 text-amber-900">A customer is required. Start from an opportunity or customer record.</p>}
          {['need', 'timing', 'budget', 'productFit'].map(key => (
            <AnswerQuestion key={key} questionKey={key}
              question={quoteSettings.questions[key]} answer={answers[key]}
              onChange={answer => updateAnswer(key, answer)} />
          ))}
          {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <Tooltip text="Build an editable CRM draft from rules and synced products">
              <button type="submit" disabled={busy || !customerId}
                className="rounded bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">
                {busy ? 'Building…' : 'Build quote draft'}
              </button>
            </Tooltip>
            <span className="text-sm text-gray-600">Optional paid AI ranking is separate and can be requested after a draft exists.</span>
          </div>
        </form>
      )}
    </main>
  );
}
